import { costValueColor, focusRing, Icon } from '@/design-system';
import type { CostKey, PublicCosts, PublicLanguage } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

/**
 * De drie kostenkaarten: direct rood, op termijn amber, verbetering neutraal.
 * Dit is de kop van het rapport - koper en hypotheekadviseur kijken hier eerst.
 *
 * Met het volledige rapport erbij is elke kaart een knop: een tik toont de
 * bevindingen achter het bedrag.
 *
 * Een bedrijf kan de kosten voor de koper verbergen. Dan verdwijnt dit blok
 * helemaal; er blijft geen lege kaart of streepje achter.
 */
export interface CostBlocksProps {
    costs: PublicCosts;
    language: PublicLanguage;
    copy: PublicCopy;
    /** `1` of `2` als het blok de kop van het tabblad Kosten is. */
    headingLevel: 1 | 2 | 3;
    /** Toont de bevindingen achter een bedrag; zonder volledig rapport ontbreekt dit. */
    onShow?: (key: CostKey) => void;
    /** Hoeveel bevindingen er achter elk bedrag staan. */
    findingCounts?: Partial<Record<CostKey, number>>;
}

const cardStyles: Record<CostKey, string> = {
    direct: 'border-[var(--danger-border)] bg-[var(--danger-tint-2)]',
    termijn: 'border-[var(--warn-border)] bg-[var(--warn-tint-2)]',
    onderhoud: 'border-[var(--border-default)] bg-[var(--surface-card)]',
};

const labelStyles: Record<CostKey, string> = {
    direct: 'text-[var(--danger-text)]',
    termijn: 'text-[var(--warn-text)]',
    onderhoud: 'text-[var(--text-tertiary-safe)]',
};

export function CostBlocks({
    costs,
    language,
    copy,
    headingLevel,
    onShow,
    findingCounts,
}: CostBlocksProps) {
    if (!costs.visible || costs.blocks.length === 0) {
        return null;
    }

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel} description={copy.costsSubtitle}>
                {copy.costsTitle}
            </SectionTitle>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
                {costs.blocks.map((block) => {
                    const amount = formatAmount(block.amount, language);
                    const findings = findingCounts?.[block.key] ?? 0;
                    const content = (
                        <>
                            <span
                                className={`block text-[12px] leading-[1.35] ${labelStyles[block.key]}`}
                            >
                                {copy.costLabels[block.key]}
                            </span>
                            <span
                                className="mt-[6px] block font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold"
                                style={{ color: costValueColor[block.key] }}
                            >
                                {amount}
                            </span>
                            {block.count === null ? null : (
                                <span
                                    className={`mt-[5px] block text-[11px] leading-[1.35] ${labelStyles[block.key]}`}
                                >
                                    {copy.costParts(block.count)}
                                </span>
                            )}
                        </>
                    );

                    if (!onShow || findings === 0) {
                        return (
                            <div
                                key={block.key}
                                className={`rounded-[9px] border p-[15px] ${cardStyles[block.key]}`}
                            >
                                {content}
                            </div>
                        );
                    }

                    return (
                        <button
                            key={block.key}
                            type="button"
                            aria-haspopup="dialog"
                            onClick={() => onShow(block.key)}
                            className={cn(
                                'group flex min-h-[44px] cursor-pointer flex-col items-stretch rounded-[9px] border p-[15px] text-left',
                                'font-[family-name:var(--font-body)] transition-colors duration-100 hover:border-[var(--accent)]',
                                cardStyles[block.key],
                                focusRing,
                            )}
                        >
                            {content}
                            <span className="mt-[10px] flex items-center gap-1 text-[12px] leading-[1.35] font-medium text-[var(--accent-dark)] group-hover:underline">
                                {copy.costFindings(findings)}
                                <Icon name="chevronDown" size="sm" className="-rotate-90" />
                            </span>
                        </button>
                    );
                })}
            </div>

            {costs.total === null ? null : (
                <div className="mt-[14px] flex flex-wrap items-baseline justify-between gap-[10px] border-t border-[var(--border-subtle)] pt-[14px]">
                    <span className="text-[14px] font-medium text-[var(--text-primary)]">
                        {copy.costsTotal}
                    </span>
                    <span className="font-[family-name:var(--font-display)] text-[22px] font-semibold text-[var(--text-primary)]">
                        {formatAmount(costs.total, language)}
                    </span>
                </div>
            )}
        </PublicBlock>
    );
}
